import { TelehealthCta } from "@/components/site/TelehealthCta";
import { createFileRoute, Link } from "@tanstack/react-router";
import { LastUpdated } from "@/components/site/LastUpdated";
import { faqPageJson } from "@/lib/faq-schema";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { WeightLossOptionsButtons } from "@/components/site/WeightLossOptionsButtons";
import { ORG_REF, PHYSICIAN_ID, PHYSICIAN_SAME_AS, medicalWebPageScript, SETTING_OUTPATIENT } from "@/lib/medical-schema";

export const Route = createFileRoute("/services/esg")({
  head: () => ({
    meta: [
      { title: "ESG Weight Loss Procedure in Northern Virginia | Virginia Bariatric" },
      {
        name: "description",
        content:
          "An incisionless, same-day outpatient weight-loss procedure that reshapes the stomach using internal sutures. Performed by Dr. Fitzer in the DC metro.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "ESG Weight Loss Procedure in Northern Virginia | Virginia Bariatric" },
        { property: "og:description", content: "An incisionless, same-day outpatient weight-loss procedure that reshapes the stomach using internal sutures. Performed by Dr. Fitzer in the DC metro." },
        { property: "og:url", content: "https://virginiabariatric.com/services/esg" },
        { property: "og:type", content: "website" },
        { property: "og:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { property: "og:image:alt", content: "Endoscopic Sleeve Gastroplasty at Virginia Bariatric Surgery" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { name: "twitter:image:alt", content: "Endoscopic Sleeve Gastroplasty at Virginia Bariatric Surgery" },
        { name: "twitter:title", content: "ESG Weight Loss Procedure in Northern Virginia | Virginia Bariatric" },
        { name: "twitter:description", content: "An incisionless, same-day outpatient weight-loss procedure that reshapes the stomach using internal sutures. Performed by Dr. Fitzer in the DC metro." },
      ] : []),
    ],
    links: canonicalAndAlternates("/services/esg"),
    scripts: import.meta.env.SSR ? [
      ...medicalWebPageScript("/services/esg"),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalProcedure",
          "@id": "https://virginiabariatric.com/services/esg#procedure",
          mainEntityOfPage: { "@id": "https://virginiabariatric.com/services/esg#webpage" },
          procedureType: "https://schema.org/NoninvasiveProcedure",
          name: "Endoscopic Sleeve Gastroplasty (ESG)",
          alternateName: ["Accordion Procedure", "Endo-Sleeve"],
          description: "An incisionless, endoscopic weight-loss procedure that reshapes the stomach using internal sutures placed through the mouth.",
          url: "https://virginiabariatric.com/services/esg",
          bodyLocation: "Stomach",
          preparation: "General anesthesia; overnight fast prior to procedure.",
          followup: "Staged diet advancing from clear liquids to solids over ~7 weeks; regular dietitian and surgeon follow-up.",
          howPerformed: "Performed through the mouth with a flexible endoscope and endoscopic suturing device. No external incisions. " + SETTING_OUTPATIENT,
          recognizingAuthority: {
            "@type": "Person",
            "@id": PHYSICIAN_ID,
            name: "Matthew Fitzer, MD",
            honorificSuffix: "MD",
            url: "https://virginiabariatric.com/about",
            mainEntityOfPage: "https://virginiabariatric.com/about",
            sameAs: PHYSICIAN_SAME_AS,
            telephone: "+1-703-709-9771",
            jobTitle: "Bariatric Surgeon",
            worksFor: ORG_REF,
            address: {
              "@type": "PostalAddress",
              streetAddress: "171 Elden Street, Suite 3B",
              addressLocality: "Herndon",
              addressRegion: "VA",
              postalCode: "20170",
              addressCountry: "US",
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "How much weight can I lose with ESG?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Published studies report an average of 13–17% of total body weight lost between 12 and 24 months, with many patients maintaining most of that loss long-term when they follow the nutrition and activity plan.",
              },
            },
            {
              "@type": "Question",
              name: "Is ESG reversible?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "ESG is organ-sparing—no stomach tissue is removed—and the sutures can be removed or revised endoscopically, so future treatment options are preserved.",
              },
            },
            {
              "@type": "Question",
              name: "How long is recovery after ESG?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Most patients go home the same day or after a single night, resume light activity and desk work within a few days, and follow a staged diet from clear liquids to solid foods over about seven weeks.",
              },
            },
            {
              "@type": "Question",
              name: "Who is a candidate for ESG?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "ESG is generally offered to adults with a BMI of roughly 30–40 who have not achieved durable results with diet and exercise, or to patients with more severe obesity who wish to avoid conventional surgery. Suitability depends on medical history and prior weight-loss efforts as well as BMI.",
              },
            },
            {
              "@type": "Question",
              name: "Is ESG covered by insurance?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "ESG is not yet consistently covered by commercial insurance plans. Our office can review self-pay options and financing during your consultation—see our insurance coverage guide for details on which weight-loss procedures major plans typically cover.",
              },
            },
          ],
        }),
      },
    ] : [],
  }),
  component: ESGPage,
});


function ESGPage() {
  return (
    <article className="mx-auto max-w-3xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-4 pb-10 md:text-[1.125rem] leading-[1.6]">
      <header>
        <h1 className="text-[2.025rem] md:text-[2.7rem] font-serif text-center">Endoscopic Sleeve Gastroplasty (ESG) in Northern Virginia</h1>
        <p className="mt-1 text-foreground/70 italic">A minimally invasive, incisionless approach to sustained weight loss</p>
        <p className="mt-1 text-sm font-medium text-brand-logo">Serving the DC metro — including Washington, D.C., Northern Virginia, and Maryland</p>
      </header>

      <p className="mt-3 text-foreground/80">
        Endoscopic sleeve gastroplasty (ESG), sometimes called the “accordion procedure” or “endo-sleeve,”
        is an incisionless weight-loss procedure performed entirely through the mouth. Using a flexible
        endoscope fitted with a suturing device, your physician places a series of sutures along the stomach
        to fold and reshape it into a narrow, tube-like sleeve—reducing its volume by roughly 70–80% without
        removing any tissue or making a single external incision. The result is earlier fullness, consumption
        of smaller portions, and, for many patients, meaningful and durable weight loss.
      </p>

      <TelehealthCta />


      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg bg-muted/50 p-4 text-center">
          <p className="text-[1.5rem] font-serif font-semibold text-brand-logo">13–17%</p>
          <p className="mt-1 text-sm text-foreground/80">average total body weight loss at 12–24 months</p>
        </div>
        <div className="rounded-lg bg-muted/50 p-4 text-center">
          <p className="text-[1.5rem] font-serif font-semibold text-brand-logo">~2%</p>
          <p className="mt-1 text-sm text-foreground/80">rate of serious adverse events</p>
        </div>
        <div className="rounded-lg bg-muted/50 p-4 text-center">
          <p className="text-[1.5rem] font-serif font-semibold text-brand-logo">No incisions</p>
          <p className="mt-1 text-sm text-foreground/80">performed entirely through the mouth</p>
        </div>
      </div>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">How endoscopic sleeve gastroplasty works</h2>
      <p className="mt-3 text-foreground/80">
        ESG is performed under general anesthesia and typically takes 45 to 90 minutes. Most patients go home
        the same day or after a single overnight stay. The upper endoscope (a gastroscope) is passed down the
        esophagus into the stomach, where a suturing system allows placement of full-thickness stitches to cinch
        the stomach inward. The result is a stomach with a functional shape similar to what is achieved by surgical
        sleeve gastrectomy. But because no anatomy is removed, the ESG is organ-sparing and (in principle)
        reversible. It is revisable.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">How much weight can you lose with ESG?</h2>
      <p className="mt-3 text-foreground/80">
        The evidence base for ESG has matured considerably. In the landmark MERIT randomized controlled trial
        published in <em>The Lancet</em> (2022), patients treated with ESG plus lifestyle changes lost an average
        of 13.6% of their total body weight at 12 months—compared with 0.8% for lifestyle changes alone—and 68%
        of patients maintained at least 25% excess weight loss at two years. Serious procedure-related complications
        occurred in about 2% of patients, with no deaths and no need for intensive care or surgery.
      </p>
      <p className="mt-3 text-foreground/80">
        These findings are consistent with pooled analyses (a.k.a., meta-analyses). A systematic review and
        meta-analysis in <em>Clinical Gastroenterology and Hepatology</em> (2019) covering nearly 1,800 patients
        reported average total body weight loss of about 15% at 6 months, sustained at roughly 16.5% at 12 months
        and 17% at 18–24 months, with a severe adverse-event rate of 2.2%. In practical terms, a patient weighing
        250 pounds could expect to lose 50+ pounds and—importantly—keep most of it off with ongoing lifestyle support.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Health benefits of ESG beyond weight loss</h2>
      <p className="mt-3 text-foreground/80">
        As with weight loss achieved any other way, weight loss from ESG is accompanied by improvement in
        obesity-related conditions. In the MERIT trial, 80% of ESG patients saw improvement in one or more metabolic
        comorbidities—such as type 2 diabetes, high blood pressure, high cholesterol, and obstructive sleep apnea.
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-6 text-foreground/80">
        <li>Incisionless and organ-sparing—no external scars and no permanent removal of the stomach</li>
        <li>Same-day or single-night discharge with a faster return to normal activity than surgery</li>
        <li>Lower risk profile than laparoscopic bariatric surgery in appropriately selected patients</li>
        <li>Potentially reversible or revisable, preserving future treatment options</li>
        <li>New-onset acid reflux is uncommon—occurring less often than after surgical sleeve gastrectomy</li>
      </ul>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Who is a candidate for ESG?</h2>
      <p className="mt-3 text-foreground/80">
        ESG is generally offered to adults with a body mass index (BMI) of roughly 30 to 40 who have not achieved
        durable results with diet and exercise alone. It is particularly well suited to patients with class I and
        class II obesity, and to those with more severe obesity who are not candidates for (or desire to avoid)
        conventional bariatric surgery. </p>

      <p className="mt-3 text-foreground/80">
        Suitability hangs on more than just BMI, however—see our{" "}
        <Link to="/resources/qualifications" className="text-brand-deep underline hover:no-underline">bariatric surgery requirements</Link>{" "}
        for a full breakdown of BMI thresholds and comorbidity criteria. Dr. Fitzer will explain
        how your medical history, prior weight-loss efforts, and eating patterns bear on the suitability of ESG.
        In patients with certain conditions, including large hiatal hernias, prior stomach surgery, active ulcers, or
        specific gastrointestinal disorders, ESG is not appropriate. A thorough evaluation—ideally within an
        experienced and multidisciplinary program—is what is needed to determine whether ESG is the right choice for you.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">ESG recovery and what to expect</h2>
      <p className="mt-3 text-foreground/80">
        Mild nausea, cramping, and abdominal discomfort in the first few days are common and usually manageable
        with medication. Patients follow a staged diet. It begins with clear liquids, advanced to full liquids and
        pureed foods, and it gradually returns to solid foods over seven weeks. </p>

      <p className="mt-3 text-foreground/80">
        It’s common for people to resume
        work and light activity within a few days. In the first year after the procedure, you can expect regular
        nutritional guidance with our dietitian and follow-up with Dr. Fitzer or his P.A. (Gratuitous word of advice:
        Whatever your weight-loss path, make follow-up a priority. Research consistently shows that robust follow-up
        strongly predicts weight loss success in the long term after ESG, not to mention every other weight-loss method.)
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">ESG risks and considerations</h2>
      <p className="mt-3 text-foreground/80">
        ESG has a strong safety record, but no procedure is without risk. Serious complications are uncommon
        (roughly 1.5–2.3% in the published literature) and can include bleeding, a leak or fluid collection around
        the stomach, or—rarely—a perforation; most such events can be managed without open surgery. More common,
        temporary effects include nausea, vomiting, and abdominal pain in the first days after the procedure. ESG is
        a tool, not a cure: weight regain is possible if long-term lifestyle changes are not sustained. Your physician
        will review your individual risks and expected benefits in detail during your consultation.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">ESG vs. Surgical Sleeve Gastrectomy</h2>
      <p className="mt-3 text-foreground/80">
        The most common question we hear is how ESG stacks up against a traditional laparoscopic{" "}
        <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">gastric sleeve surgery (sleeve gastrectomy)</Link>. Surgical sleeve
        gastrectomy typically produces greater long-term weight loss—on the order of 25–30% of total body weight—because a
        larger portion of the stomach is permanently removed. ESG, by contrast, reshapes the stomach without removing tissue,
        producing average weight loss of roughly 15–17% of total body weight. </p>

      <p className="mt-3 text-foreground/80">
        In exchange for a lower ceiling on weight loss,
        ESG offers no external incisions, a shorter recovery, a lower rate of new-onset acid reflux, and preserved anatomy
        that keeps future options open. For patients with class I or class II obesity—or those who are not candidates for or
        want to avoid conventional surgery—that trade-off is often the right one.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">ESG and Weight-Loss Medications</h2>
      <p className="mt-3 text-foreground/80">
        A recent JAMA Network Open study (Haseeb et al., 2024) compared ESG with semaglutide (Wegovy) and found ESG produced
        greater and more durable weight loss at two years, with weight regain common after semaglutide was stopped. For many
        patients, ESG and GLP-1 medications are complementary rather than competing tools—medications can bridge the
        pre-procedure period or reinforce results later on. Dr. Fitzer will walk through how these options fit your
        situation during your consultation.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Next steps: schedule an ESG consultation</h2>
      <p className="mt-3 text-foreground/80">
        If you are considering ESG, the first step is a consultation to review your health history, goals, and whether
        this procedure fits your needs. Our team provides comprehensive evaluation and long-term support to help
        you achieve and maintain your results. Contact our office to schedule an appointment.
      </p>
      <p className="mt-3 text-foreground/80">
        Not using insurance? See the{" "}
        <Link to="/self-pay-bariatric-surgery" hash="esg-cost" className="text-brand-deep underline hover:no-underline">
          ESG cost
        </Link>{" "}
        self-pay: $12,900, which covers the physician fee, the outpatient facility fee at INOVA Fair
        Oaks Hospital, an anesthesia estimate, and a year of routine follow-up with Dr. Fitzer. You
        can also explore{" "}
        <Link to="/resources/prosper" className="text-brand-deep underline hover:no-underline">
          payment plan options
        </Link>.
      </p>


      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Endoscopic sleeve gastroplasty in Northern Virginia and the D.C. area</h2>
      <p className="mt-3 text-foreground/80">ESG is performed as an outpatient procedure at INOVA Fair Oaks Hospital in Fairfax. There are no incisions, and almost all patients go home the same day. Consultations and follow-up are at our office at 171 Elden Street, Herndon. Patients from Arlington, Alexandria and Washington, D.C. can start with a telehealth visit.</p>
      <p className="mt-3 text-foreground/80">Comparing options? See <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">gastric sleeve surgery in Northern Virginia</Link>, the <Link to="/services/balloon" className="text-brand-deep underline hover:no-underline">Orbera gastric balloon in Northern Virginia</Link>, or <Link to="/medical-weight-loss" className="text-brand-deep underline hover:no-underline">medical weight loss in Northern Virginia</Link>.</p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Frequently Asked Questions</h2>
      <dl className="mt-2 space-y-3 text-foreground/80">
        <div>
          <dt className="font-medium text-foreground">How much weight can I lose with ESG?</dt>
          <dd className="mt-1">Published studies report an average of 13–17% of total body weight lost between 12 and 24 months, with many patients maintaining most of that loss long-term when they follow the nutrition and activity plan.</dd>
        </div>
        <div>
          <dt className="font-medium text-foreground">Is ESG reversible?</dt>
          <dd className="mt-1">ESG is organ-sparing—no stomach tissue is removed—and the sutures can be removed or revised endoscopically, so future treatment options are preserved.</dd>
        </div>
        <div>
          <dt className="font-medium text-foreground">How long is recovery after ESG?</dt>
          <dd className="mt-1">Most patients go home the same day or after a single night, resume light activity and desk work within a few days, and follow a staged diet from clear liquids to solid foods over about seven weeks.</dd>
        </div>
        <div>
          <dt className="font-medium text-foreground">Who is a candidate for ESG?</dt>
          <dd className="mt-1">ESG is generally offered to adults with a BMI of roughly 30–40 who have not achieved durable results with diet and exercise, or to patients with more severe obesity who wish to avoid conventional surgery. Suitability depends on medical history and prior weight-loss efforts as well as BMI.</dd>
        </div>
        <div>
          <dt className="font-medium text-foreground">Is ESG covered by insurance?</dt>
          <dd className="mt-1">ESG is not yet consistently covered by commercial insurance plans. Our office can review self-pay options and financing during your consultation—see our <Link to="/resources/insurance-coverage" className="text-brand-deep underline hover:no-underline">insurance coverage guide</Link> for details on which weight-loss procedures major plans typically cover. If you are paying for the procedure yourself, our <Link to="/self-pay-bariatric-surgery" className="text-brand-deep underline hover:no-underline">self-pay bariatric surgery</Link> page lists the published ESG price and what is included.</dd>
        </div>
      </dl>


      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to="/appointments" className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90">
          Request a consultation
        </Link>
        <WeightLossOptionsButtons />
      </div>

      <div className="mt-4 text-sm text-foreground/70">
        <p className="font-medium">References</p>
        <ol className="mt-1 list-decimal space-y-1 pl-5">
          <li>
            Abu Dayyeh BK, et al. Endoscopic sleeve gastroplasty for treatment of class 1 and 2 obesity (MERIT). <em>Lancet</em>. 2022;400:441–451. doi:10.1016/S0140-6736(22)01280-6
          </li>
          <li>
            Hedjoudje A, et al. Efficacy and Safety of Endoscopic Sleeve Gastroplasty: A Systematic Review and Meta-Analysis. <em>Clin Gastroenterol Hepatol</em>. 2020;18:1043–1053. doi:10.1016/j.cgh.2019.08.022
          </li>
          <li>
            Li P, et al. Efficacy and safety of endoscopic sleeve gastroplasty for obesity patients: a meta-analysis. <em>Surg Endosc</em>. 2020;34:1253–1260. doi:10.1007/s00464-019-06889-6
          </li>
          <li>
            Marincola G, et al. Laparoscopic sleeve gastrectomy versus endoscopic sleeve gastroplasty. <em>Endosc Int Open</em>. 2021;9:E87–E95. doi:10.1055/a-1300-1085
          </li>
          <li>
            Baratte C, et al. Position statement and guidelines about Endoscopic Sleeve Gastroplasty. <em>J Visc Surg</em>. 2025;162:71–78. doi:10.1016/j.jviscsurg.2024.12.003
          </li>
          <li>
            Haseeb M, et al. Semaglutide vs Endoscopic Sleeve Gastroplasty for Weight Loss. <em>JAMA Netw Open</em>. 2024;7:e246221. doi:10.1001/jamanetworkopen.2024.6221
          </li>
        </ol>
      </div>
      <LastUpdated className="mt-10" />
      </article>
  );
}
